/**
 * Axios client with JWT injection, global error handling and an offline layer:
 *   - GET responses are mirrored to localStorage and served back on network failure
 *   - Mutating calls flagged with `offlineQueueable: true` are pushed into the
 *     offline queue (see `lib/offlineQueue.js`) when the request fails because
 *     the device is offline
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const TOKEN_KEY = "prerec_token";

export const tokenStore = {
    get: () => localStorage.getItem(TOKEN_KEY),
    set: (t) => localStorage.setItem(TOKEN_KEY, t),
    clear: () => localStorage.removeItem(TOKEN_KEY),
};

export const api = axios.create({
    baseURL: API_BASE,
    timeout: 60000,
});

api.interceptors.request.use((config) => {
    const token = tokenStore.get();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (res) => {
        // Mirror successful GETs to the offline cache so they remain readable
        // when the network drops next.
        if (res.config?.method?.toLowerCase() === "get") {
            // Lazy import to avoid circular dependency at module init time
            try {
                // eslint-disable-next-line global-require
                const { cacheGet } = require("./offlineCache");
                cacheGet(res.config.url, res.config.params, res.data);
            } catch (_e) { /* ignore */ }
        }
        return res;
    },
    (err) => {
        if (err.response?.status === 401) {
            tokenStore.clear();
            if (window.location.pathname !== "/login") {
                window.location.href = "/login";
            }
            return Promise.reject(err);
        }

        const isNetworkError = !err.response;
        const cfg = err.config || {};
        const method = (cfg.method || "get").toLowerCase();

        // GET → serve from cache transparently
        if (isNetworkError && method === "get") {
            try {
                // eslint-disable-next-line global-require
                const { getCached } = require("./offlineCache");
                const cached = getCached(cfg.url, cfg.params);
                if (cached) {
                    return Promise.resolve({
                        data: cached,
                        status: 200,
                        statusText: "OK (offline cache)",
                        headers: {},
                        config: cfg,
                        request: null,
                        offlineCacheHit: true,
                    });
                }
            } catch (_e) { /* ignore */ }
        }

        // Mutating action flagged for offline queueing
        if (isNetworkError && method !== "get" && cfg.offlineQueueable) {
            try {
                // eslint-disable-next-line global-require
                const { enqueueAction } = require("./offlineQueue");
                const queued = enqueueAction({
                    method: cfg.method?.toUpperCase() || "POST",
                    path: cfg.url,
                    body: cfg.data ? (typeof cfg.data === "string" ? JSON.parse(cfg.data) : cfg.data) : null,
                    label: cfg.offlineLabel || `${cfg.method?.toUpperCase()} ${cfg.url}`,
                });
                // Return a synthetic 202 response so callers can keep their UI optimistic
                return Promise.resolve({
                    data: cfg.optimisticData ?? { queued: true, queuedId: queued.id },
                    status: 202,
                    statusText: "Accepted (queued offline)",
                    headers: {},
                    config: cfg,
                    request: null,
                    offlineQueued: true,
                });
            } catch (_e) { /* ignore */ }
        }

        return Promise.reject(err);
    },
);
